import { useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog-v1'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'
import { Textarea } from '@wartek-id/textarea'
import { useToast } from '@wartek-id/toast-v2'

import {
  API_PATH_PREDIKAT_KINERJA_ORGANISASI,
  useAPISendPredikatKinerjaOrganisasi,
} from 'api/kinerja/useAPIKinerja'
import { assetUrl } from 'utils/url'

export const CONFIRMATION_TEXT =
  'Saya setuju untuk mengirim Predikat Kinerja Organisasi.'

export type DialogKirimPredikatOrganisasiProps = {
  isOpen: boolean
  ids: Array<string>
  onClose?: () => void
}

export function DialogKirimPredikatOrganisasi({
  isOpen,
  ids,
  onClose,
}: DialogKirimPredikatOrganisasiProps) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const [step, setStep] = useState<'CONFIRMATION' | 'LOADING' | 'SUCCESS'>(
    'CONFIRMATION'
  )

  const upsertMutation = useAPISendPredikatKinerjaOrganisasi()

  const handleSubmit = () => {
    setStep('LOADING')

    upsertMutation.mutate(
      {
        tahapPredikatIds: ids,
      },
      {
        onError: () => {
          onClose?.()
          toast({
            message: 'Gagal kirim Predikat Kinerja Organisasi. Coba lagi.',
          })
        },
      }
    )
  }

  const handleCloseOnSuccess = () => {
    onClose()
    queryClient.invalidateQueries(API_PATH_PREDIKAT_KINERJA_ORGANISASI)
  }

  // Reset step upon close
  useEffect(() => {
    if (!isOpen) {
      setStep('CONFIRMATION')
    }
  }, [isOpen])

  return (
    <Dialog
      isOpen={isOpen}
      closeOnOverlayClick={false}
      size="sm"
      onClose={onClose}
    >
      <DialogOverlay />
      <DialogContent>
        {step === 'CONFIRMATION' && (
          <StepConfirmation onNext={() => handleSubmit()} onPrev={onClose} />
        )}

        {step === 'LOADING' && (
          <StepLoading
            isSuccess={upsertMutation.isSuccess}
            minDuration={2000}
            onNext={() => setStep('SUCCESS')}
          />
        )}

        {step === 'SUCCESS' && (
          <StepSuccess count={ids.length} onConfirm={handleCloseOnSuccess} />
        )}
      </DialogContent>
    </Dialog>
  )
}

type StepConfirmationProps = {
  onPrev?: () => void
  onNext?: () => void
}
function StepConfirmation({ onPrev, onNext }: StepConfirmationProps) {
  const [confirmation, setConfirmation] = useState('')

  const isEnabled = confirmation === CONFIRMATION_TEXT

  return (
    <div className="p-6 space-y-4">
      <img
        src={assetUrl('/illustrations/img-publish-data.png')}
        alt=""
        width={84}
        height={84}
      />

      <Text variant="display-sm" as="h2">
        Lanjut kirim Predikat Kinerja Organisasi?
      </Text>

      <Text variant="body-lg" color="subdued">
        Kepala Sekolah akan memulai Penetapan Predikat Kinerja untuk
        guru-gurunya berdasarkan Predikat Kinerja Organisasi yang Anda kirim.
      </Text>
      <Text variant="body-lg" color="subdued">
        Mohon ketik kalimat ini pada kolom kalimat persetujuan:
      </Text>
      <div className="bg-blue-5 text-blue-60 p-1 rounded select-none">
        <Text variant="heading-sm" color="inherit">
          {CONFIRMATION_TEXT}
        </Text>
      </div>
      <div className="space-y-1">
        <Text as="label" htmlFor="uncompleted-confirmation-text">
          Kalimat persetujuan
        </Text>
        <Textarea
          id="uncompleted-confirmation-text"
          placeholder="Ketik di sini."
          autoComplete="off"
          onChange={(e) => setConfirmation(e.target.value)}
        />
      </div>

      <div className="flex space-x-3 !justify-start">
        <Button color="white" size="lg" onClick={onPrev}>
          Kembali
        </Button>
        <Button
          color="blue"
          disabled={!isEnabled}
          size="lg"
          type="button"
          onClick={() => onNext?.()}
        >
          Lanjut
        </Button>
      </div>
    </div>
  )
}

type StepLoadingProps = {
  isSuccess: boolean
  minDuration: number // ms
  onNext?: () => void
}
function StepLoading({ isSuccess, minDuration, onNext }: StepLoadingProps) {
  const [reachedMinDuration, setReachedMinDuration] = useState(false)

  // Update flag after some duration
  useEffect(() => {
    const timeout = setTimeout(() => {
      setReachedMinDuration(true)
    }, minDuration)

    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Invoke onNext only if isSuccess is true and has reached minimum loading duration
  useEffect(() => {
    if (isSuccess && reachedMinDuration) {
      onNext?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, reachedMinDuration])

  return (
    <div className="pt-20 pb-12 px-6 flex flex-col items-center text-center">
      <span className="mb-9">
        <Spinner size="lg" />
      </span>
      <Text variant="display-sm" as="h2" className="mb-1 max-w-sm">
        Mengirim Predikat Kinerja Organisasi...
      </Text>
      <Text variant="body-lg" color="subdued">
        Mohon tunggu sebentar, ya.
      </Text>
    </div>
  )
}

type StepSuccessProps = {
  count: number
  onConfirm: () => void
}
function StepSuccess({ count, onConfirm }: StepSuccessProps) {
  return (
    <div className="p-6">
      <img
        src={assetUrl('/illustrations/img-thumbs-up.png')}
        alt=""
        width={84}
        height={84}
        className="mb-4"
      />

      <div className="mb-6">
        <Text variant="display-sm" as="h2" className="mb-2">
          {count} Predikat Kinerja Organisasi sudah dikirim
        </Text>

        <Text variant="body-lg" color="subdued">
          Anda dapat melanjutkan pengiriman Predikat Kinerja Organisasi jika ada
          yang belum dikirim. Mohon cek secara berkala.
        </Text>
      </div>

      <Button color="blue" size="lg" type="button" onClick={onConfirm}>
        Saya mengerti
      </Button>
    </div>
  )
}
